import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Snackbar,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  LinearProgress,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Search,
  FilterList,
  GetApp,
  BugReport,
  Warning,
  Error,
  CheckCircle,
  Assignment,
  Schedule,
  Person,
  Comment,
} from '@mui/icons-material';
import { issueTrackingService, Issue as ApiIssue } from '../services/issueTrackingService';
import projectsService from '../services/projectsService';
import { API_BASE_URL } from '../services/config';
import companyService from '../services/companyService';
import axios from 'axios';

interface Issue {
  id: string;
  title: string;
  description: string;
  project: string;
  location: string;
  reportedBy: string;
  assignedTo: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed' | 'on_hold';
  priority: 'low' | 'medium' | 'high' | 'critical';
  severity: 'minor' | 'major' | 'critical' | 'blocker';
  category: 'safety' | 'quality' | 'schedule' | 'budget' | 'technical' | 'other';
  dateReported: string;
  dateResolved?: string;
  dueDate: string;
  estimatedHours: number;
  actualHours?: number;
  comments: Comment[];
  attachments: string[];
}

interface Comment {
  id: string;
  author: string;
  content: string;
  timestamp: string;
}



const IssueTracking: React.FC = () => {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newIssueTitle, setNewIssueTitle] = useState('');
  const [newIssueDescription, setNewIssueDescription] = useState('');
  const [newIssuePriority, setNewIssuePriority] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');
  const [newIssueCategory, setNewIssueCategory] = useState<'safety' | 'quality' | 'schedule' | 'budget' | 'technical' | 'other'>('other');
  const [newIssueProjectId, setNewIssueProjectId] = useState('');
  const [newIssueCompanyId, setNewIssueCompanyId] = useState('');
  const [projectsList, setProjectsList] = useState<Array<{ id: string; name: string }>>([]);
  const [companiesList, setCompaniesList] = useState<Array<{ id: string; name: string }>>([]);
  const [showCompanySelect, setShowCompanySelect] = useState(false);
  const [fetchingCreateLists, setFetchingCreateLists] = useState(false);
  const [currentCompanyName, setCurrentCompanyName] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Helper to normalize API issue into local Issue interface
  const transformApiIssue = (issue: any): Issue => {
    // Names: prefer populated reporter/assignee, fallback to simple labels
    const reporterUser = issue?.reporter?.user;
    const assigneeUser = issue?.assignee?.user;
    const reporterName = (reporterUser && (reporterUser.firstName || reporterUser.lastName))
      ? [reporterUser.firstName, reporterUser.lastName].filter(Boolean).join(' ')
      : (issue?.reportedBy?.name || 'Unknown');
    const assigneeName = (assigneeUser && (assigneeUser.firstName || assigneeUser.lastName))
      ? [assigneeUser.firstName, assigneeUser.lastName].filter(Boolean).join(' ')
      : (issue?.assignedTo?.name || 'Unassigned');

    // Status mapping from backend values to local enum (case-insensitive, supports spaces/hyphens)
    const statusMap: Record<string, 'open' | 'in_progress' | 'resolved' | 'closed' | 'on_hold'> = {
      open: 'open',
      in_progress: 'in_progress',
      'in-progress': 'in_progress',
      'in progress': 'in_progress',
      resolved: 'resolved',
      closed: 'closed',
      on_hold: 'on_hold',
      'on-hold': 'on_hold',
      'on hold': 'on_hold',
    };
    const statusRaw = String(issue?.status || 'open').toLowerCase();
    const status = statusMap[statusRaw] || 'open';

    // Priority mapping from model values to local enum (case-insensitive)
    const priorityMap: Record<string, 'low' | 'medium' | 'high' | 'critical'> = {
      low: 'low',
      medium: 'medium',
      normal: 'medium',
      high: 'high',
      urgent: 'critical',
      critical: 'critical',
    };
    const priorityRaw = String(issue?.priority || 'medium').toLowerCase();
    const priority = priorityMap[priorityRaw] || 'medium';

    // Severity mapping (backend stores Low/Medium/High/Critical) - case-insensitive
    const severityMap: Record<string, 'minor' | 'major' | 'critical' | 'blocker'> = {
      low: 'minor',
      medium: 'major',
      high: 'critical',
      critical: 'blocker',
    };
    const severityRaw = String(issue?.severity || issue?.impact || 'low').toLowerCase();
    const severity = severityMap[severityRaw] || 'minor';

    // Category mapping to local union (case-insensitive)
    const categoryMap: Record<string, 'safety' | 'quality' | 'schedule' | 'budget' | 'technical' | 'other'> = {
      safety: 'safety',
      quality: 'quality',
      schedule: 'schedule',
      budget: 'budget',
      design: 'technical',
      technical: 'technical',
      construction: 'other',
      other: 'other',
    };
    const categoryRaw = String(issue?.category || 'other').toLowerCase();
    const category = categoryMap[categoryRaw] || 'other';

    const attachments = Array.isArray(issue?.attachments)
      ? issue.attachments.map((att: any) => att?.originalName || att?.name || att?.url).filter(Boolean)
      : [];

    const comments = Array.isArray(issue?.comments)
      ? issue.comments.map((comment: any) => ({
          id: comment?._id,
          author: comment?.author?.name || comment?.user?.name || 'Unknown',
          content: comment?.content || comment?.text || '',
          timestamp: comment?.createdAt || comment?.timestamp || ''
        }))
      : [];

    return {
      id: issue?._id,
      title: issue?.title,
      description: issue?.description,
      project: typeof issue?.project === 'object' ? (issue.project?.name || issue.project?._id) : issue?.project,
      location: issue?.location?.area || issue?.location || '',
      reportedBy: reporterName,
      assignedTo: assigneeName,
      status,
      priority,
      severity,
      category,
      dateReported: issue?.createdAt || issue?.dates?.reported || '',
      dateResolved: issue?.resolvedDate || issue?.dates?.actualResolution,
      dueDate: issue?.dueDate || issue?.dates?.dueDate || issue?.createdAt || '',
      estimatedHours: issue?.estimatedHours || 0,
      actualHours: issue?.actualHours,
      comments,
      attachments
    } as Issue;
  };

  useEffect(() => {
    const fetchIssues = async () => {
      try {
        setLoading(true);
        const issuesData = await issueTrackingService.getIssues();
        
        // Transform API data to match local interface
        const transformedIssues = issuesData.map(transformApiIssue);
        
        setIssues(transformedIssues);
      } catch (error) {
        console.error('Error fetching issues:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchIssues();
  }, []);

  // Prepare lists when create dialog opens
  useEffect(() => {
    const prepareCreateDialog = async () => {
      if (!createDialogOpen) return;
      setFetchingCreateLists(true);
      try {
        // Determine user role and default company
        let shouldSelectCompany = false;
        try {
          const baseURL = API_BASE_URL;
          const token = localStorage.getItem('token');
          const me = await axios.get(`${baseURL}/auth/me`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          const user = me?.data?.user || me?.data;
          const roleRaw = String(user?.role || '').toLowerCase();
          const isAdminRole = roleRaw.includes('admin');
          const company = user?.company;
          const companyId = typeof company === 'object' ? (company?._id || company?.id) : company;
          const companyName = typeof company === 'object' ? (company?.name || company?.legalName) : '';
          setCurrentCompanyName(companyName || '');
          if (companyId) {
            setNewIssueCompanyId(String(companyId));
          }
          // Admins or users without company should select company
          shouldSelectCompany = isAdminRole || !companyId;
          setShowCompanySelect(shouldSelectCompany);
        } catch (e) {
          // If /auth/me fails, show selector to prevent blocking submit
          setShowCompanySelect(true);
        }

        // Fetch recent projects for selector (fallback to manual input if inaccessible)
        try {
          const projRes = await projectsService.getRecent(50);
          const raw = (projRes as any)?.data;
          const list = Array.isArray(raw?.data) ? raw.data : (Array.isArray(raw) ? raw : []);
          const mapped = list.map((p: any) => ({ id: String(p._id || p.id), name: String(p.name || 'Untitled Project') }));
          setProjectsList(mapped);
        } catch (e) {
          setProjectsList([]);
        }

        // Fetch companies when selector should be shown
        if (showCompanySelect) {
          try {
            const companies = await companyService.getAllCompanies();
            const mapped = Array.isArray(companies)
              ? companies.map((c: any) => ({ id: String(c._id || c.id), name: String(c.name || c.legalName || 'Company') }))
              : [];
            setCompaniesList(mapped);
          } catch (e) {
            setCompaniesList([]);
          }
        }
      } finally {
        setFetchingCreateLists(false);
      }
    };

    prepareCreateDialog();
  }, [createDialogOpen, showCompanySelect]);

  // Ensure issues is always an array
  const issuesArray = Array.isArray(issues) ? issues : [];

  const filteredIssues = issuesArray.filter((issue) => {
    const matchesSearch = issue.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         issue.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         issue.project.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || issue.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || issue.priority === priorityFilter;
    const matchesCategory = categoryFilter === 'all' || issue.category === categoryFilter;
    return matchesSearch && matchesStatus && matchesPriority && matchesCategory;
  });

  const totalIssues = issuesArray.length;
  const openIssues = issuesArray.filter(i => i.status === 'open').length;
  const inProgressIssues = issuesArray.filter(i => i.status === 'in_progress').length;
  const criticalIssues = issuesArray.filter(i => i.priority === 'critical').length;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'error';
      case 'in_progress': return 'warning';
      case 'resolved': return 'success';
      case 'closed': return 'info';
      case 'on_hold': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'blocker': return <Error color="error" />;
      case 'critical': return <Warning color="error" />;
      case 'major': return <Warning color="warning" />;
      case 'minor': return <BugReport color="info" />;
      default: return <BugReport />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDaysOverdue = (dueDate: string, status: string) => {
    if (status === 'resolved' || status === 'closed') return 0;
    const due = new Date(dueDate);
    const now = new Date();
    const diffTime = now.getTime() - due.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const getProgressPercentage = (issue: Issue) => {
    if (issue.status === 'closed' || issue.status === 'resolved') return 100;
    if (issue.status === 'in_progress') return issue.actualHours ? (issue.actualHours / issue.estimatedHours) * 100 : 25;
    if (issue.status === 'open') return 0;
    return 0;
  };

  const handleViewIssue = (issue: Issue) => {
    setSelectedIssue(issue);
    setViewDialogOpen(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Issue Tracking
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          sx={{ bgcolor: 'primary.main' }}
          onClick={(e) => {
            // Move focus away from the trigger before opening the modal
            const activeEl = document.activeElement as HTMLElement | null;
            if (activeEl && typeof activeEl.blur === 'function') {
              activeEl.blur();
            }
            if (typeof e.currentTarget.blur === 'function') {
              e.currentTarget.blur();
            }
            setCreateDialogOpen(true);
          }}
        >
          Report Issue
        </Button>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Assignment color="primary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Total Issues
                </Typography>
                <Typography variant="h5">
                  {totalIssues}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Error color="error" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Open Issues
                </Typography>
                <Typography variant="h5">
                  {openIssues}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Schedule color="warning" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  In Progress
                </Typography>
                <Typography variant="h5">
                  {inProgressIssues}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Warning color="error" sx={{ fontSize: 40 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Critical Issues
                </Typography>
                <Typography variant="h5">
                  {criticalIssues}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filters and Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search issues..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
            }}
            sx={{ minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
              <MenuItem value="on_hold">On Hold</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={priorityFilter}
              label="Priority"
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <MenuItem value="all">All Priority</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="low">Low</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="all">All Categories</MenuItem>
              <MenuItem value="safety">Safety</MenuItem>
              <MenuItem value="quality">Quality</MenuItem>
              <MenuItem value="schedule">Schedule</MenuItem>
              <MenuItem value="budget">Budget</MenuItem>
              <MenuItem value="technical">Technical</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            sx={{ ml: 'auto' }}
          >
            More Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<GetApp />}
          >
            Export
          </Button>
        </Box>
      </Paper>

      {/* Issues Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Issue</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Assigned To</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredIssues.map((issue) => {
                const overdueDays = getDaysOverdue(issue.dueDate, issue.status);
                const progress = getProgressPercentage(issue);
                
                return (
                  <TableRow key={issue.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getSeverityIcon(issue.severity)}
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {issue.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {issue.category} • Reported by {issue.reportedBy}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {issue.project}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {issue.location}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                          {issue.assignedTo.split(' ').map(n => n[0]).join('')}
                        </Avatar>
                        <Typography variant="body2">
                          {issue.assignedTo}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={issue.status.replace('_', ' ')}
                        color={getStatusColor(issue.status) as any}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={issue.priority}
                        color={getPriorityColor(issue.priority) as any}
                        size="small"
                        variant="outlined"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ width: 100 }}>
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          sx={{ mb: 0.5 }}
                        />
                        <Typography variant="caption">
                          {Math.round(progress)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(issue.dueDate)}
                      </Typography>
                      {overdueDays > 0 && (
                        <Typography variant="caption" color="error">
                          {overdueDays} days overdue
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewIssue(issue)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small">
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error">
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* View Issue Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Issue Details - {selectedIssue?.title}
        </DialogTitle>
        <DialogContent>
          {selectedIssue && (
            <Box sx={{ pt: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Project
                  </Typography>
                  <Typography variant="body1">
                    {selectedIssue.project}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Location
                  </Typography>
                  <Typography variant="body1">
                    {selectedIssue.location}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Description
                </Typography>
                <Typography variant="body1">
                  {selectedIssue.description}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={selectedIssue.status.replace('_', ' ')}
                    color={getStatusColor(selectedIssue.status) as any}
                    size="small"
                    sx={{ textTransform: 'capitalize', mt: 0.5 }}
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Priority
                  </Typography>
                  <Chip
                    label={selectedIssue.priority}
                    color={getPriorityColor(selectedIssue.priority) as any}
                    size="small"
                    variant="outlined"
                    sx={{ textTransform: 'capitalize', mt: 0.5 }}
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Severity
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    {getSeverityIcon(selectedIssue.severity)}
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {selectedIssue.severity}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Reported By
                  </Typography>
                  <Typography variant="body1">
                    {selectedIssue.reportedBy}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Assigned To
                  </Typography>
                  <Typography variant="body1">
                    {selectedIssue.assignedTo}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Date Reported
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedIssue.dateReported)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Due Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedIssue.dueDate)}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Estimated Hours
                  </Typography>
                  <Typography variant="body1">
                    {selectedIssue.estimatedHours}h
                  </Typography>
                </Box>
                {selectedIssue.actualHours && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Actual Hours
                    </Typography>
                    <Typography variant="body1">
                      {selectedIssue.actualHours}h
                    </Typography>
                  </Box>
                )}
              </Box>
              
              {selectedIssue.comments.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Comments
                  </Typography>
                  {selectedIssue.comments.map((comment) => (
                    <Paper key={comment.id} sx={{ p: 2, mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                          {comment.author.split(' ').map(n => n[0]).join('')}
                        </Avatar>
                        <Typography variant="body2" fontWeight="medium">
                          {comment.author}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(comment.timestamp)}
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        {comment.content}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              )}
              
              {selectedIssue.attachments.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Attachments
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedIssue.attachments.map((attachment, index) => (
                      <Chip
                        key={index}
                        label={attachment}
                        size="small"
                        variant="outlined"
                        clickable
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button startIcon={<Comment />}>
            Add Comment
          </Button>
          <Button variant="contained" startIcon={<Edit />}>
            Edit Issue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Issue Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Report Issue</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {fetchingCreateLists && <LinearProgress />}
            <TextField
              label="Title"
              value={newIssueTitle}
              onChange={(e) => setNewIssueTitle(e.target.value)}
              autoFocus
              required
            />
            <TextField
              label="Description"
              value={newIssueDescription}
              onChange={(e) => setNewIssueDescription(e.target.value)}
              multiline
              minRows={3}
              required
            />
            <FormControl>
              <InputLabel>Priority</InputLabel>
              <Select
                label="Priority"
                value={newIssuePriority}
                onChange={(e) => setNewIssuePriority(e.target.value as any)}
              >
                <MenuItem value="critical">Critical</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={newIssueCategory}
                onChange={(e) => setNewIssueCategory(e.target.value as any)}
              >
                <MenuItem value="safety">Safety</MenuItem>
                <MenuItem value="quality">Quality</MenuItem>
                <MenuItem value="schedule">Schedule</MenuItem>
                <MenuItem value="budget">Budget</MenuItem>
                <MenuItem value="technical">Technical</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            {projectsList.length > 0 ? (
              <FormControl>
                <InputLabel>Project</InputLabel>
                <Select
                  label="Project"
                  value={newIssueProjectId}
                  onChange={(e) => setNewIssueProjectId(String(e.target.value))}
                >
                  {projectsList.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                label="Project ID"
                value={newIssueProjectId}
                onChange={(e) => setNewIssueProjectId(e.target.value)}
                helperText="Enter a valid project Mongo ID"
                required
              />
            )}

            {showCompanySelect ? (
              <FormControl>
                <InputLabel>Company</InputLabel>
                <Select
                  label="Company"
                  value={newIssueCompanyId}
                  onChange={(e) => setNewIssueCompanyId(String(e.target.value))}
                >
                  {companiesList.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                label="Company"
                value={currentCompanyName}
                InputProps={{ readOnly: true }}
                helperText={currentCompanyName ? 'Derived from your profile' : 'Company will be derived during submit'}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                console.debug('[IssueTracking] Preparing issue payload for submission');
                const payload = {
                  title: newIssueTitle,
                  description: newIssueDescription,
                  priority: newIssuePriority,
                  category: newIssueCategory,
                  project: newIssueProjectId,
                } as any;
                if (newIssueCompanyId) {
                  payload.company = newIssueCompanyId;
                }
                console.debug('[IssueTracking] Submitting issue payload', payload);
                const createResponse = await issueTrackingService.createIssue(payload);
                console.debug('[IssueTracking] Issue submission response', createResponse);
                setSnackbar({ open: true, message: 'Issue reported successfully', severity: 'success' });
                setCreateDialogOpen(false);
                // Refresh list after creation
                setLoading(true);
                console.debug('[IssueTracking] Fetching updated issues list');
                // Try to append created issue optimistically, especially in debug mode
                const maybeWrapper = createResponse as any;
                const createdApiIssue = (maybeWrapper && maybeWrapper.data) ? maybeWrapper.data : maybeWrapper;
                const createdLocalIssue = createdApiIssue?._id ? transformApiIssue(createdApiIssue) : null;
                if (createdLocalIssue) {
                  setIssues(prev => [createdLocalIssue, ...prev]);
                }

                const isDebugCreate = typeof maybeWrapper?.message === 'string' && maybeWrapper.message.includes('debug');
                if (isDebugCreate) {
                  console.debug('[IssueTracking] Debug mode detected; skipping list refresh as backend returns empty list');
                } else {
                  const refreshed = await issueTrackingService.getIssues();
                  console.debug('[IssueTracking] Updated issues list fetched', refreshed);
                  const transformedIssues = refreshed.map(transformApiIssue);
                  setIssues(transformedIssues);
                }
              } catch (err: any) {
                console.error('[IssueTracking] Error creating issue:', err);
                const msg = err?.response?.data?.message || err?.message || 'Failed to report issue';
                setSnackbar({ open: true, message: msg, severity: 'error' });
              } finally {
                console.debug('[IssueTracking] Issue submission flow complete');
                setLoading(false);
                // reset form
                setNewIssueTitle('');
                setNewIssueDescription('');
                setNewIssuePriority('medium');
                setNewIssueCategory('other');
                setNewIssueProjectId('');
              }
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IssueTracking;